import { useCallback, useEffect, useMemo, useState } from "react";
import NutritionCharts from "../../components/nutrition/NutritionCharts";
import ErrorMessage from "../../components/ui/ErrorMessage";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { useModalNavigation, useModalPayload } from "../../context/ModalControllerContext";
import { buildCustomNutritionResponse } from "../../helpers";
import useGetNutritionByMealItem from "../../hooks/data/nutrition/useGetNutritionByMealItem";
import useOptionalHook from "../../hooks/utils/useOptionalHook";
import ModalHeader from "../components/ModalHeader";

const MIN_SPINNER_TIME = 1000;

const ModalFoodItemNutrition = ({ payload: externalPayload, onRequestClose }) => {
	const [forceLoading, setForceLoading] = useState(true);

	const controllerPayload = useOptionalHook(useModalPayload);
	const item = externalPayload ?? controllerPayload;


	const isCustom = item.food_item_id == null;

	const { data, isLoading, isError, error } = useGetNutritionByMealItem(
		isCustom ? undefined : item.id
	);

	const resolvedData = useMemo(() => {
		if (isCustom) return buildCustomNutritionResponse(item);

		return data;
	}, [isCustom, item, data]);

	const navigation = useOptionalHook(useModalNavigation);

	const handleClose = useCallback(() => {
		if (onRequestClose) onRequestClose();
		else navigation?.close();
	}, [onRequestClose, navigation]);

	useEffect(() => {
		let timeout;

		if (isCustom) timeout = setTimeout(() => setForceLoading(false), MIN_SPINNER_TIME);
		else if (!isLoading) timeout = setTimeout(() => setForceLoading(false), 200);

		return () => clearTimeout(timeout);
	}, [isCustom, isLoading]);

	const showLoading = forceLoading || (!isCustom && isLoading);
	const showError = !isCustom && isError;

	if (!item) return null;

	return (
		<>
			<ModalHeader
				title={item.name}
				showBack={navigation?.canGoBack ?? false}
				onGoBack={navigation?.goBack}
				onClose={handleClose}
			/>

			<div className={`modal-body meal-nutrition ${showLoading ? "is-loading" : ""}`}>
				{showLoading && <LoadingSpinner />}

				{showError && (
					<ErrorMessage
						message={error?.message || "Kunde inte hämta näringsinformation just nu."}
					/>
				)}

				{!showLoading && !showError && resolvedData && (
					<NutritionCharts
						data={resolvedData}
						items={[item]}
						showMealItems={false}
						showDesiredEnergy={false}
						showMacros
						energyTitle="Energifördelning (E %)"
					/>
				)}
			</div>
		</>
	);
};

export default ModalFoodItemNutrition;
